import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { fadeIn } from '../../utils/gsapAnimations';
import {
  fetchCountries,
  fetchStates,
  updateCustomerProfile,
} from '../../utils/api';

const channelOptions = [
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'returning_customer', label: 'Returning Customer' },
  { value: 'word_of_mouth', label: 'Word of Mouth' },
  { value: 'referral_customer', label: 'Customer Referral' },
  { value: 'referral_staff', label: 'Staff Referral' },
  { value: 'referral_partner', label: 'Partner Referral' },
  { value: 'website', label: 'Website' },
  { value: 'google_search', label: 'Google Search' },
  { value: 'google_maps', label: 'Google Maps' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'x', label: 'X (Twitter)' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'facebook_ads', label: 'Facebook Ads' },
  { value: 'google_ads', label: 'Google Ads' },
  { value: 'instagram_ads', label: 'Instagram Ads' },
  { value: 'influencer_marketing', label: 'Influencer Marketing' },
  { value: 'flyers', label: 'Flyers' },
  { value: 'billboard', label: 'Billboard' },
  { value: 'radio', label: 'Radio' },
  { value: 'tv', label: 'TV' },
  { value: 'event', label: 'Event/Activation' },
  { value: 'staff_marketing', label: 'Staff Marketing' },
  { value: 'direct_sales', label: 'Direct Sales Team' },
  { value: 'cold_outreach', label: 'Cold Outreach' },
  { value: 'business_partner', label: 'Business Partner' },
  { value: 'estate_management', label: 'Estate Management' },
  { value: 'other', label: 'Other' },
];

const createProfileForm = (user) => {
  const tenant = user?.tenant || {};

  return {
    full_name: tenant.full_name || user?.name || '',
    business_name: tenant.business_name || '',
    email: tenant.email || user?.email || '',
    telephone: tenant.telephone || '',
    address: tenant.address || '',
    country: tenant.country?.id || tenant.country_id || '',
    state: tenant.state?.id || tenant.state_id || '',
    channel: tenant.channel || '',
  };
};

function ProfileSection() {
  const sectionRef = useRef(null);

  const {
    user,
    token,
    refreshUser,
  } = useAuth();

  const [form, setForm] = useState(() => createProfileForm(user));

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);

  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [saving, setSaving] = useState(false);

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (sectionRef.current) {
      fadeIn(sectionRef.current, 0.3);
    }
  }, []);

  /*
   * Important:
   * Whenever AuthContext receives a refreshed user,
   * repopulate the form with the new profile data.
   */
  useEffect(() => {
    setForm(createProfileForm(user));
  }, [user]);

  useEffect(() => {
    let active = true;

    const loadCountries = async () => {
      setLoadingCountries(true);

      try {
        const response = await fetchCountries();

        if (active) {
          setCountries(response?.data || []);
        }
      } catch (err) {
        if (active) {
          setError(
            err?.response?.data?.message ||
              'Could not load countries.'
          );
        }
      } finally {
        if (active) {
          setLoadingCountries(false);
        }
      }
    };

    loadCountries();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadStates = async () => {
      if (!form.country) {
        setStates([]);
        return;
      }

      setLoadingStates(true);

      try {
        const response = await fetchStates(form.country);

        if (active) {
          setStates(response?.data || []);
        }
      } catch (err) {
        if (active) {
          setStates([]);
          setError(
            err?.response?.data?.message ||
              'Could not load states.'
          );
        }
      } finally {
        if (active) {
          setLoadingStates(false);
        }
      }
    };

    loadStates();

    return () => {
      active = false;
    };
  }, [form.country]);

  const handleChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,

      ...(field === 'country'
        ? {
            state: '',
          }
        : {}),
    }));

    setMessage('');
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (saving) return;

    setMessage('');
    setError('');

    if (!form.full_name.trim()) {
      setError('Full name is required.');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        full_name: form.full_name.trim(),
        business_name:
          form.business_name.trim() || null,
        telephone: form.telephone.trim() || null,
        address: form.address.trim() || null,
        country: form.country || null,
        state: form.state || null,
        channel: form.channel || null,
      };

      const response = await updateCustomerProfile(
        payload,
        token
      );

      if (response?.status === false) {
        setError(
          response?.message ||
            'Could not update profile.'
        );
        return;
      }

      /*
       * Reload the authenticated user immediately.
       * This updates every component using AuthContext
       * without requiring logout/login.
       */
      if (typeof refreshUser === 'function') {
        await refreshUser();
      }

      setMessage(
        response?.message ||
          'Profile updated successfully.'
      );
    } catch (err) {
      const validationErrors =
        err?.response?.data?.errors;

      if (validationErrors) {
        const firstError = Object.values(
          validationErrors
        )
          .flat()
          .find(Boolean);

        setError(
          firstError ||
            'Please check the profile details.'
        );
      } else {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            'Could not update profile.'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const initials = (
    form.full_name ||
    form.business_name ||
    'Customer'
  )
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');

  const inputClass = `
    w-full px-4 py-3.5
    border border-gray-200
    rounded-xl
    bg-white
    text-burgundy
    font-montserrat
    outline-none
    transition
    placeholder:text-gray-400
    focus:border-primary
    focus:ring-4
    focus:ring-primary/10
    disabled:bg-gray-100
    disabled:text-gray-500
    disabled:cursor-not-allowed
  `;

  const labelClass =
    'block text-sm font-montserrat font-medium text-burgundy mb-2';

  return (
    <div
      ref={sectionRef}
      className="
        bg-white
        rounded-3xl
        shadow-xl
        border border-gray-100
        overflow-hidden
      "
    >
      <div className="bg-gradient-to-r from-primary/10 via-cream/70 to-white px-6 py-8 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <div
            className="
              w-20 h-20
              rounded-full
              bg-primary
              text-white
              flex items-center justify-center
              text-2xl
              font-lora
              font-semibold
              shadow-lg
              shrink-0
            "
          >
            {initials || 'C'}
          </div>

          <div className="min-w-0">
            <h2 className="text-3xl font-lora font-semibold text-burgundy">
              Your Profile
            </h2>

            <p className="text-sm text-secondary/70 mt-2 max-w-xl">
              Manage your contact, business and delivery
              information.
            </p>

            <div className="mt-3 inline-flex items-center px-3 py-1 rounded-full bg-white border border-gray-200 text-xs font-montserrat text-secondary">
              {form.email || 'Customer account'}
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 sm:p-8">
        {message && (
          <div
            role="status"
            className="
              mb-6
              p-4
              rounded-2xl
              bg-green-50
              border border-green-200
              text-green-700
              text-sm
              font-montserrat
            "
          >
            {message}
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="
              mb-6
              p-4
              rounded-2xl
              bg-red-50
              border border-red-200
              text-red-700
              text-sm
              font-montserrat
            "
          >
            {error}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="space-y-8"
        >
          <section>
            <div className="mb-5">
              <h3 className="text-lg font-lora font-semibold text-burgundy">
                Personal information
              </h3>

              <p className="text-sm text-secondary/60 mt-1">
                Your primary account and contact details.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label
                  htmlFor="profile-full-name"
                  className={labelClass}
                >
                  Full name
                  <span className="text-red-500 ml-1">
                    *
                  </span>
                </label>

                <input
                  id="profile-full-name"
                  type="text"
                  value={form.full_name}
                  onChange={(event) =>
                    handleChange(
                      'full_name',
                      event.target.value
                    )
                  }
                  placeholder="Enter your full name"
                  className={inputClass}
                  maxLength={150}
                  required
                  disabled={saving}
                />
              </div>

              <div>
                <label
                  htmlFor="profile-business-name"
                  className={labelClass}
                >
                  Business name
                </label>

                <input
                  id="profile-business-name"
                  type="text"
                  value={form.business_name}
                  onChange={(event) =>
                    handleChange(
                      'business_name',
                      event.target.value
                    )
                  }
                  placeholder="Enter business name"
                  className={inputClass}
                  maxLength={150}
                  disabled={saving}
                />
              </div>

              <div>
                <label
                  htmlFor="profile-email"
                  className={labelClass}
                >
                  Email address
                </label>

                <input
                  id="profile-email"
                  type="email"
                  value={form.email}
                  className={inputClass}
                  disabled
                />

                <p className="text-xs text-secondary/50 mt-2">
                  Your login email cannot be changed here.
                </p>
              </div>

              <div>
                <label
                  htmlFor="profile-telephone"
                  className={labelClass}
                >
                  Phone number
                </label>

                <input
                  id="profile-telephone"
                  type="tel"
                  value={form.telephone}
                  onChange={(event) =>
                    handleChange(
                      'telephone',
                      event.target.value
                    )
                  }
                  placeholder="Enter phone number"
                  className={inputClass}
                  maxLength={30}
                  disabled={saving}
                />
              </div>
            </div>
          </section>

          <div className="border-t border-gray-100" />

          <section>
            <div className="mb-5">
              <h3 className="text-lg font-lora font-semibold text-burgundy">
                Location and delivery
              </h3>

              <p className="text-sm text-secondary/60 mt-1">
                Used to improve delivery and order
                fulfilment.
              </p>
            </div>

            <div className="space-y-5">
              <div>
                <label
                  htmlFor="profile-address"
                  className={labelClass}
                >
                  Address
                </label>

                <textarea
                  id="profile-address"
                  value={form.address}
                  onChange={(event) =>
                    handleChange(
                      'address',
                      event.target.value
                    )
                  }
                  placeholder="Enter your delivery address"
                  className={`${inputClass} min-h-28 resize-y`}
                  maxLength={500}
                  disabled={saving}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="profile-country"
                    className={labelClass}
                  >
                    Country
                  </label>

                  <select
                    id="profile-country"
                    value={form.country}
                    onChange={(event) =>
                      handleChange(
                        'country',
                        event.target.value
                      )
                    }
                    disabled={
                      loadingCountries || saving
                    }
                    className={inputClass}
                  >
                    <option value="">
                      {loadingCountries
                        ? 'Loading countries...'
                        : 'Select country'}
                    </option>

                    {countries.map((country) => (
                      <option
                        key={country.id}
                        value={country.id}
                      >
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="profile-state"
                    className={labelClass}
                  >
                    State
                  </label>

                  <select
                    id="profile-state"
                    value={form.state}
                    onChange={(event) =>
                      handleChange(
                        'state',
                        event.target.value
                      )
                    }
                    disabled={
                      !form.country ||
                      loadingStates ||
                      saving
                    }
                    className={inputClass}
                  >
                    <option value="">
                      {loadingStates
                        ? 'Loading states...'
                        : form.country
                          ? 'Select state'
                          : 'Select a country first'}
                    </option>

                    {states.map((state) => (
                      <option
                        key={state.id}
                        value={state.id}
                      >
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </section>

          <div className="border-t border-gray-100" />

          <section>
            <div className="mb-5">
              <h3 className="text-lg font-lora font-semibold text-burgundy">
                Customer source
              </h3>

              <p className="text-sm text-secondary/60 mt-1">
                Help us understand how you discovered the
                business.
              </p>
            </div>

            <div>
              <label
                htmlFor="profile-channel"
                className={labelClass}
              >
                How did you hear about us?
              </label>

              <select
                id="profile-channel"
                value={form.channel}
                onChange={(event) =>
                  handleChange(
                    'channel',
                    event.target.value
                  )
                }
                className={inputClass}
                disabled={saving}
              >
                <option value="">
                  Select a channel
                </option>

                {channelOptions.map((channel) => (
                  <option
                    key={channel.value}
                    value={channel.value}
                  >
                    {channel.label}
                  </option>
                ))}
              </select>
            </div>
          </section>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="
                w-full sm:w-auto
                min-w-44
                bg-primary
                text-white
                px-8 py-3.5
                rounded-xl
                font-montserrat
                font-semibold
                shadow-lg
                shadow-primary/20
                transition
                hover:bg-primary/90
                hover:-translate-y-0.5
                disabled:opacity-60
                disabled:cursor-not-allowed
                disabled:transform-none
              "
            >
              {saving
                ? 'Saving changes...'
                : 'Save Profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProfileSection;